import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { GroupBase, OptionProps } from 'react-select';
import { components } from 'react-select';
import { MenuRowCustom } from '~/components/new/Menu';
import Select, { type SelectOption, type SelectProps } from '~/components/ui/forms/Select';
import { classNames } from '~/utils/styles';

type MomentTimezone = typeof import('moment');

let momentTimezone: MomentTimezone | null = null;
let momentTimezonePromise: Promise<MomentTimezone> | null = null;

function loadMomentTimezone(): Promise<MomentTimezone> {
    if (momentTimezone) {
        return Promise.resolve(momentTimezone);
    }
    momentTimezonePromise ??= import('moment-timezone')
        .then((mod) => {
            momentTimezone = (mod as { default: MomentTimezone }).default;
            return momentTimezone;
        })
        .catch((error) => {
            momentTimezonePromise = null;
            throw error;
        });
    return momentTimezonePromise;
}

function useMoment(): MomentTimezone | null {
    const [momentReady, setMomentReady] = useState(momentTimezone !== null);

    useEffect(() => {
        if (momentTimezone) {
            setMomentReady(true);
            return;
        }
        let cancelled = false;
        void loadMomentTimezone()
            .then(() => {
                if (!cancelled) {
                    setMomentReady(true);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setMomentReady(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return momentReady ? momentTimezone : null;
}

export type MeetingTimezoneOption = SelectOption<string, string>;

type MeetingTimezoneGrouped = GroupBase<MeetingTimezoneOption>;

type MeetingTimezoneOffsetOption = SelectOption<string, ReactNode>;

type MeetingTimezoneOffsetGrouped = GroupBase<MeetingTimezoneOffsetOption>;

type SelectTimezoneOptionType = MeetingTimezoneOption | MeetingTimezoneOffsetOption;

type SelectTimezoneOptionProps = OptionProps<
    SelectTimezoneOptionType,
    boolean,
    GroupBase<SelectTimezoneOptionType>
>;

type SelectTimezoneProps = Omit<
    SelectProps<string>,
    'options' | 'components' | 'classNamePrefix'
> & {
    withOffset?: boolean;
    allZones?: boolean;
    options?: (MeetingTimezoneGrouped | MeetingTimezoneOffsetGrouped)[];
};

let cachedIANATimezoneGroups: MeetingTimezoneGrouped[] | null = null;

function getIANATimezoneGroups(): MeetingTimezoneGrouped[] {
    if (cachedIANATimezoneGroups) {
        return cachedIANATimezoneGroups;
    }

    const groups = new Map<string, MeetingTimezoneOption[]>();
    for (const timezone of Intl.supportedValuesOf('timeZone')) {
        const [region] = timezone.split('/');
        const regionLabel = (region ?? 'OTHER').replace(/_/g, ' ').toUpperCase();
        const options = groups.get(regionLabel) ?? [];
        options.push({ value: timezone, label: timezone });
        groups.set(regionLabel, options);
    }

    cachedIANATimezoneGroups = [...groups.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([label, options]) => ({
            label,
            options: options.sort((a, b) => a.label.localeCompare(b.label)),
        }));

    return cachedIANATimezoneGroups;
}

function getMeetingTimezonesWithOffset(moment: MomentTimezone): MeetingTimezoneOffsetGrouped[] {
    return MEETING_TIMEZONES.map(({ label, options }) => ({
        label,
        options: options.map(({ value, label: optLabel }) => ({
            value,
            label: (
                <>
                    {optLabel}{' '}
                    <span className='text-core-tertiary'>
                        ({moment().tz(value).format('[UTC]Z')})
                    </span>
                </>
            ),
        })),
    }));
}

export function SelectTimezone({
    value,
    disabled,
    error,
    withOffset = false,
    allZones = false,
    className = '',
    options: timezoneOptions,
    ...props
}: SelectTimezoneProps) {
    const moment = useMoment();

    const options: (MeetingTimezoneGrouped | MeetingTimezoneOffsetGrouped)[] = useMemo(() => {
        return (
            timezoneOptions ??
            (allZones
                ? getIANATimezoneGroups()
                : withOffset && moment
                  ? getMeetingTimezonesWithOffset(moment)
                  : MEETING_TIMEZONES)
        );
    }, [timezoneOptions, allZones, withOffset, moment]);
    const flat = options.flatMap((i) => [...i.options]);
    const val = typeof value === 'object' ? value : flat.find((o) => o.value === value) || '';

    const inputID = props.name ? { inputId: props.name } : {};
    return (
        <Select
            className={classNames({
                'select-with-error': !!error,
                [className]: Boolean(className),
            })}
            value={val}
            options={[...options]}
            isDisabled={disabled}
            components={{ Option: SelectTimezoneOption }}
            {...inputID}
            {...props}
        />
    );
}

function SelectTimezoneOption(props: SelectTimezoneOptionProps) {
    const moment = useMoment();
    const { label, value } = props.data;
    return (
        <components.Option
            {...props}
            className='menu-row'>
            <MenuRowCustom>
                <span
                    className='menu-row-label-text'
                    style={{ overflowWrap: 'break-word' }}>
                    {label}
                </span>
                <span className='text-core-tertiary'>
                    {moment ? moment().tz(value).format('H:mma') : null}
                </span>
            </MenuRowCustom>
        </components.Option>
    );
}

export function getTimeZoneLabel(input: string | undefined | null) {
    if (!input) {
        return undefined;
    }

    const curatedLabel = MEETING_TIMEZONES.map((t) => t.options)
        .flat()
        .find((t) => t.value === input)?.label;
    if (curatedLabel) {
        return curatedLabel;
    }

    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: input,
            timeZoneName: 'longGeneric',
        }).formatToParts(new Date());
        const name = parts.find((part) => part.type === 'timeZoneName')?.value;
        if (name) {
            return name;
        }
    } catch {
        // invalid timezone
    }

    return input;
}

export const MEETING_TIMEZONES: MeetingTimezoneGrouped[] = [
    {
        label: 'US/CANADA',
        options: [
            { value: 'America/Los_Angeles', label: 'Pacific Time - US & Canada' },
            { value: 'America/Denver', label: 'Mountain Time - US & Canada' },
            { value: 'America/Chicago', label: 'Central Time - US & Canada' },
            { value: 'America/New_York', label: 'Eastern Time - US & Canada' },
            { value: 'America/Anchorage', label: 'Alaska Time' },
            { value: 'America/Phoenix', label: 'Arizona, Yukon Time' },
            { value: 'America/St_Johns', label: 'Newfoundland Time' },
            { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
        ],
    },
    {
        label: 'AMERICA',
        options: [
            { value: 'America/Adak', label: 'America/Adak' },
            { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires Time' },
            { value: 'America/Asuncion', label: 'Asuncion Time' },
            { value: 'America/Bogota', label: 'Bogota, Jamaica, Lima Time' },
            { value: 'America/Campo_Grande', label: 'America/Campo Grande' },
            { value: 'America/Caracas', label: 'Caracas Time' },
            { value: 'America/Godthab', label: 'America/Godthab' },
            { value: 'America/Goose_Bay', label: 'Atlantic Time' },
            { value: 'America/Guatemala', label: 'Saskatchewan, Guatemala, Costa Rica Time' },
            { value: 'America/Havana', label: 'America/Havana' },
            { value: 'America/Mazatlan', label: 'America/Mazatlan' },
            { value: 'America/Mexico_City', label: 'Mexico City Time' },
            { value: 'America/Montevideo', label: 'Montevideo Time' },
            { value: 'America/Miquelon', label: 'America/Miquelon' },
            { value: 'America/Noronha', label: 'America/Noronha' },
            { value: 'America/Santiago', label: 'Santiago Time' },
            { value: 'America/Santa_Isabel', label: 'America/Santa Isabel' },
            { value: 'America/Santo_Domingo', label: 'Atlantic Standard Time' },
            { value: 'America/Sao_Paulo', label: 'Brasilia Time' },
        ],
    },
    {
        label: 'AFRICA',
        options: [
            { value: 'Africa/Cairo', label: 'Africa/Cairo' },
            { value: 'Africa/Johannesburg', label: 'Central Africa Time' },
            { value: 'Africa/Lagos', label: 'West Africa Time' },
            { value: 'Africa/Windhoek', label: 'Africa/Windhoek' },
        ],
    },
    {
        label: 'ASIA',
        options: [
            { value: 'Asia/Amman', label: 'Jordan Time' },
            { value: 'Asia/Baghdad', label: 'Baghdad, East Africa Time' },
            { value: 'Asia/Baku', label: 'Asia/Baku' },
            { value: 'Asia/Beirut', label: 'Lebanon Time' },
            { value: 'Asia/Damascus', label: 'Syria Time' },
            { value: 'Asia/Dhaka', label: 'Asia/Dhaka' },
            { value: 'Asia/Dubai', label: 'Dubai Time' },
            { value: 'Asia/Gaza', label: 'Asia/Gaza' },
            { value: 'Asia/Irkutsk', label: 'Asia/Irkutsk' },
            { value: 'Asia/Jakarta', label: 'Indochina Time' },
            { value: 'Asia/Jerusalem', label: 'Israel Time' },
            { value: 'Asia/Kabul', label: 'Kabul Time' },
            { value: 'Asia/Kamchatka', label: 'Pacific/Majuro' },
            { value: 'Asia/Karachi', label: 'Pakistan, Maldives Time' },
            { value: 'Asia/Kathmandu', label: 'Kathmandu Time' },
            { value: 'Asia/Kolkata', label: 'India, Sri Lanka Time' },
            { value: 'Asia/Krasnoyarsk', label: 'Krasnoyarsk Time' },
            { value: 'Asia/Omsk', label: 'Aska/Omsk' },
            { value: 'Asia/Rangoon', label: 'Asia Rangoon' },
            { value: 'Asia/Shanghai', label: 'China, Singapore, Perth' },
            { value: 'Asia/Tehran', label: 'Tehran Time' },
            { value: 'Asia/Tokyo', label: 'Japan, Korea Time' },
            { value: 'Asia/Vladivostok', label: 'Asia/Vladivostok' },
            { value: 'Asia/Yakutsk', label: 'Asia/Yakutsk' },
            { value: 'Asia/Yekaterinburg', label: 'Yekaterinburg Time' },
            { value: 'Asia/Yerevan', label: 'Asia/Yerevan' },
        ],
    },
    {
        label: 'ATLANTIC',
        options: [
            { value: 'Atlantic/Azores', label: 'Azores Time' },
            { value: 'Atlantic/Cape_Verde', label: 'Cape Verde Time' },
        ],
    },
    {
        label: 'AUSTRALIA',
        options: [
            { value: 'Australia/Adelaide', label: 'Adelaide Time' },
            { value: 'Australia/Brisbane', label: 'Brisbane TIme' },
            { value: 'Australia/Darwin', label: 'Australia/Darwin' },
            { value: 'Australia/Eucla', label: 'Australia/Eucla' },
            { value: 'Australia/Lord_Howe', label: 'Australia/Lord Howe' },
            { value: 'Australia/Perth', label: 'Australia/Perth' },
            { value: 'Australia/Sydney', label: 'Sydney, Melbourne Time' },
        ],
    },
    { label: 'UTC', options: [{ value: 'UTC', label: 'UTC Time' }] },
    {
        label: 'EUROPE',
        options: [
            { value: 'Europe/Berlin', label: 'Central European Time' },
            { value: 'Europe/Helsinki', label: 'Eastern European Time' },
            { value: 'Europe/London', label: 'UK, Ireland, Lisbon Time' },
            { value: 'Europe/Minsk', label: 'Minsk Time' },
            { value: 'Europe/Moscow', label: 'Moscow Time' },
            { value: 'Europe/Chisinau', label: 'Turkey Time' },
        ],
    },
    {
        label: 'PACIFIC',
        options: [
            { value: 'Pacific/Apia', label: 'Pacific/Apia' },
            { value: 'Pacific/Auckland', label: 'Auckland Time' },
            { value: 'Pacific/Chatham', label: 'Pacific/Chatham' },
            { value: 'Pacific/Easter', label: 'Pacific/Easter' },
            { value: 'Pacific/Fiji', label: 'Pacific/Fiji' },
            { value: 'Pacific/Gambier', label: 'Pacific/Gambier' },
            { value: 'Pacific/Kiritimati', label: 'Pacific/Kiritimati' },
            { value: 'Pacific/Majuro', label: 'Pacific/Majuro' },
            { value: 'Pacific/Marquesas', label: 'Pacific/Marquesas' },
            { value: 'Pacific/Norfolk', label: 'Pacific/Norfolk' },
            { value: 'Pacific/Noumea', label: 'Pacific/Noumea' },
            { value: 'Pacific/Pago_Pago', label: 'Pacific/Pago Pago' },
            { value: 'Pacific/Pitcairn', label: 'Pacific/Pitcairn' },
            { value: 'Pacific/Tarawa', label: 'Pacific/Tarawa' },
            { value: 'Pacific/Tongatapu', label: 'Pacific/Tongatapu' },
        ],
    },
];

const MEETING_TIMEZONES_ALLOWED = MEETING_TIMEZONES.map((t) =>
    t.options.map((o) => o.value),
).flat();

export function isAllowedTimezone(input: string | undefined | null) {
    return MEETING_TIMEZONES_ALLOWED.includes((input || '').trim());
}
